import { captureException } from '@sentry/browser'
import { En } from 'media-chrome/dist/lang/en.js'
import { addTranslation, setLanguage } from 'media-chrome/dist/utils/i18n.js'
import type { Cleanup } from '../helpers/cleanup'
import {
  createWaveformController,
  type WaveformController
} from './audio/controller'
import { parseAudioI18n } from './audio/i18n'

// Private types ---------------------------------------------------------------

type AudioHookContext = {
  readonly el: HTMLElement
  controlsCleanup?: Cleanup
  waveformController?: WaveformController
}

// Private functions -----------------------------------------------------------

// Registers backend translations before the vendor constructs labels and tooltips.
const configureAudioI18n = (element: HTMLElement): void => {
  const encoded = element.dataset.i18n
  if (encoded === undefined) {
    return
  }

  const i18n = parseAudioI18n(encoded)
  if (i18n.status === 'ready') {
    // The vendor needs a complete dictionary; untranslated keys keep English.
    addTranslation(i18n.locale, { ...En, ...i18n.translations })
    setLanguage(i18n.locale)
  } else {
    captureException(new TypeError('Invalid audio i18n configuration'), {
      tags: { component: 'audio', operation: 'parse-i18n' }
    })
  }
}

// Reports unavailable enhanced controls while native audio remains usable.
const reportAudioLoadFailure = (error: unknown): void => {
  captureException(error, {
    tags: { component: 'audio', operation: 'load-controls' }
  })
}

// Reveals the native fallback only on failure while this hook still owns it.
const loadAudioControls = (element: HTMLElement): Cleanup => {
  const controller = new AbortController()

  configureAudioI18n(element)

  import('media-chrome').catch((error: unknown) => {
    reportAudioLoadFailure(error)
    if (!controller.signal.aborted) {
      element.setAttribute('data-controls-unavailable', '')
    }
  })

  return () => controller.abort()
}

// Public hook -----------------------------------------------------------------

export const FacadeAudio = {
  // Registers shared controls on demand without delaying the LiveView mount.
  mounted(this: AudioHookContext): void {
    this.controlsCleanup = loadAudioControls(this.el)
    this.waveformController = createWaveformController(this.el)
  },

  // Reconciles opt-in, source, and retained or replaced timeline elements.
  updated(this: AudioHookContext): void {
    this.waveformController?.sync()
  },

  // Cancels optional recording downloads while LiveView is disconnected.
  disconnected(this: AudioHookContext): void {
    this.waveformController?.disconnect()
  },

  // Retries optional enhancement after the LiveView connection recovers.
  reconnected(this: AudioHookContext): void {
    this.waveformController?.reconnect()
  },

  // Prevents removed draft attachments from continuing to play.
  destroyed(this: AudioHookContext): void {
    this.controlsCleanup?.()
    this.controlsCleanup = undefined
    this.waveformController?.cleanup()
    this.waveformController = undefined
    this.el.querySelector('audio')?.pause()
  }
}
